// ============================================================================
// crmTempoReal · a Caixa do CRM ao vivo pelo Supabase Realtime (25/10/2026)
// ============================================================================
// Pedido do dono: "normalmente no CRM que usamos chega imediatamente; faça de
// uma forma que não fique tão caro ou pesado, mas que seja imediato e
// funcional". O banco (migrations/crm-004-tempo-real.sql) manda um aviso SEM
// DADO no canal público 'crm-caixa' (evento 'mudou', payload {t: instante})
// a cada mensagem, pedido da fila ou mudança de status. Esta aba, com a Caixa
// aberta e visível, recebe o aviso e relê a Caixa e a conversa aberta pelo
// /api/crm-sofia de sempre (sessão e recorte no servidor). Nada de PII no
// canal: quem não tem sessão no DNA só vê "mudou alguma coisa, agora".
//
// Leve de propósito:
//   · assina só com a aba visível; escondida, o canal fica 2 min (troca rápida
//     de aba não reconecta) e depois fecha (sem conexão nem batida de coração);
//   · junta a rajada: 400 ms depois do último aviso (no máximo 2 s), UMA
//     releitura; com uma releitura em andamento, mais uma no fim e só;
//   · teto por aba: 4 seguidas e depois 1 a cada 5 s (12 por minuto), para
//     um aviso falso no canal público nunca virar uma enxurrada de chamadas;
//   · a consulta periódica vira RESERVA (60 s desde a última leitura) só
//     quando o canal está provado: inscrito E com um aviso recebido nos
//     últimos 15 min. Antes do primeiro aviso (a crm-004 ainda não rodou, o
//     Realtime não entrega) e com o canal caído, o ritmo é o de antes (30 s,
//     8 s com a conversa sua): nunca fica pior do que era;
//   · reconecta sozinho (2, 5, 15, 30 e 60 s) e, ao voltar, relê uma vez (o
//     que mudou com o canal fora). A releitura de quando a aba volta a ficar
//     visível continua com a tela (CaixaEntrada), como antes.
// Interruptor: CRM_TEMPO_REAL em src/config/featureFlags.js; false = nada
// daqui roda (nem o pacote do Supabase é baixado) e a Caixa fica como antes.
// A URL e a chave publishable do projeto são as que o código já usa
// (api/cron-refresh-visagismo.js): públicas por desenho, só abrem o que o
// RLS deixa (anon sem nada) e o canal público de avisos.
// ============================================================================

import { CRM_TEMPO_REAL } from '../config/featureFlags.js';

export const TOPICO = 'crm-caixa';
export const EVENTO = 'mudou';
export const ESPERA_MS = 400;
export const ESPERA_MAX_MS = 2_000;
export const RESERVA_MS = 60_000;
export const CONFIANCA_MS = 15 * 60_000;
export const FOLGA_ESCONDIDA_MS = 2 * 60_000;
export const RECONEXAO_MS = Object.freeze([2_000, 5_000, 15_000, 30_000, 60_000]);
export const BALDE = Object.freeze({ capacidade: 4, recargaMs: 5_000 });

const SUPABASE_URL = 'https://epofcvuwlxcsiumqcfly.supabase.co';
const CHAVE_PUBLICA = 'sb_publishable_BgnQltMLLDKGTM9cRGL3Jg_JxTkj32j';

// Um cliente por página, só quando a Caixa abre com o interruptor ligado (import dinâmico:
// fora do pacote principal). Sem login do Supabase: nada é guardado no navegador.
let clientePromessa = null;
export function clientePadrao() {
  if (!clientePromessa) {
    clientePromessa = import('@supabase/supabase-js')
      .then(({ createClient }) => createClient(SUPABASE_URL, CHAVE_PUBLICA, {
        auth: { persistSession: false, autoRefreshToken: false, detectSessionInUrl: false },
      }))
      .catch((e) => { clientePromessa = null; throw e; });
  }
  return clientePromessa;
}

/** A espera até a próxima consulta: com o canal ao vivo, no mínimo 60 s (reserva); sem ele, a de antes. */
export function ritmoComCanal({ aoVivo = false, base }) {
  return aoVivo ? Math.max(base, RESERVA_MS) : base;
}

const RELOGIO = Object.freeze({
  agora: () => Date.now(),
  setTimeout: (f, ms) => setTimeout(f, ms),
  clearTimeout: (t) => clearTimeout(t),
});
const DESLIGADO = Object.freeze({ iniciar() {}, parar() {}, aoVivo: () => false, conectado: () => false });

/**
 * O canal de avisos da Caixa.
 *   aoSinal({ t })  a releitura (Caixa + conversa aberta); t = a hora do banco do aviso mais novo da leva (null na
 *              releitura ao reconectar; a Caixa ignora, o vigia manda como ?apos=); pode devolver uma promessa
 *   aoEstado({ conectado, aoVivo })  quando muda (para a tela e para o ritmo da reserva)
 * → { iniciar(), parar(), aoVivo(), conectado() }
 */
export function criarTempoReal({
  ligado = CRM_TEMPO_REAL,
  criarCliente = clientePadrao,
  aoSinal = () => {},
  aoEstado = () => {},
  doc = typeof document !== 'undefined' ? document : null,
  relogio = RELOGIO,
  espera = ESPERA_MS,
  esperaMax = ESPERA_MAX_MS,
  confianca = CONFIANCA_MS,
  folga = FOLGA_ESCONDIDA_MS,
  reconexao = RECONEXAO_MS,
  balde = BALDE,
} = {}) {
  if (!ligado) return DESLIGADO;

  let ativo = false;
  let querAberto = false;
  let conectando = false;
  let cliente = null;
  let canal = null;
  let inscrito = false;
  let releAoInscrever = false;
  let tentativa = 0;
  let ultimoAviso = 0;
  let notificado = { conectado: false, aoVivo: false };
  let remocao = Promise.resolve();
  let inicioRajada = 0;
  // 30/10/2026 (vigia, revisão 2): a hora do banco (payload t) do aviso mais novo ainda não entregue ao aoSinal
  let maiorT = 0;
  let emVoo = false;
  let pendente = false;
  let fichas = balde.capacidade;
  let fichasEm = relogio.agora();
  const tm = { espera: null, balde: null, reconexao: null, folga: null, confianca: null };
  const limpar = (k) => { if (tm[k] != null) { relogio.clearTimeout(tm[k]); tm[k] = null; } };
  const visivel = () => !doc || doc.visibilityState !== 'hidden';

  const aoVivo = () => inscrito && ultimoAviso > 0 && relogio.agora() - ultimoAviso < confianca;
  function avisarEstado() {
    const agora = { conectado: inscrito, aoVivo: aoVivo() };
    if (agora.conectado === notificado.conectado && agora.aoVivo === notificado.aoVivo) return;
    notificado = agora;
    try { aoEstado({ ...agora }); } catch { /* a tela segue */ }
  }

  // ── a releitura: teto por aba e uma de cada vez ──
  function recarregarFichas() {
    const t = relogio.agora();
    const novas = Math.floor((t - fichasEm) / balde.recargaMs);
    if (novas > 0) { fichas = Math.min(balde.capacidade, fichas + novas); fichasEm += novas * balde.recargaMs; }
    if (fichas >= balde.capacidade) fichasEm = t;
  }
  function disparar() {
    if (!ativo || !visivel()) return;
    if (emVoo) { pendente = true; return; }
    recarregarFichas();
    if (fichas < 1) {
      if (tm.balde == null) tm.balde = relogio.setTimeout(() => { tm.balde = null; disparar(); }, Math.max(0, fichasEm + balde.recargaMs - relogio.agora()));
      return;
    }
    fichas -= 1;
    emVoo = true;
    const info = { t: maiorT || null };
    maiorT = 0;
    Promise.resolve()
      .then(() => aoSinal(info))
      .catch(() => { /* a tela mostra o erro da releitura */ })
      .finally(() => {
        emVoo = false;
        if (pendente) { pendente = false; disparar(); }
      });
  }
  // junta a rajada: 400 ms depois do último aviso, no máximo 2 s depois do primeiro
  function agendarDisparo() {
    const t = relogio.agora();
    if (!inicioRajada) inicioRajada = t;
    limpar('espera');
    const ms = Math.max(0, Math.min(espera, inicioRajada + esperaMax - t));
    tm.espera = relogio.setTimeout(() => { tm.espera = null; inicioRajada = 0; disparar(); }, ms);
  }

  function recebeu(t = null) {
    const tn = Number(t);
    if (Number.isFinite(tn) && tn > 0) maiorT = Math.max(maiorT, tn);
    ultimoAviso = relogio.agora();
    limpar('confianca');
    tm.confianca = relogio.setTimeout(() => { tm.confianca = null; avisarEstado(); }, confianca + 1);
    avisarEstado();
    // escondida: nada de releitura (a tela relê quando a aba volta)
    if (visivel()) agendarDisparo();
  }

  // ── o canal ──
  function soltar() {
    const ch = canal;
    canal = null;
    inscrito = false;
    if (ch && cliente) remocao = Promise.resolve().then(() => cliente.removeChannel(ch)).catch(() => {});
  }
  function caiu() {
    soltar();
    releAoInscrever = true;
    avisarEstado();
    if (!ativo || !querAberto) return;
    limpar('reconexao');
    const ms = reconexao[Math.min(tentativa, reconexao.length - 1)];
    tentativa += 1;
    tm.reconexao = relogio.setTimeout(() => { tm.reconexao = null; if (ativo && querAberto && visivel()) conectar(); }, ms);
  }
  function mudouStatus(ch, status) {
    if (canal !== ch) return;   // aviso de um canal que já soltamos
    if (status === 'SUBSCRIBED') {
      inscrito = true;
      tentativa = 0;
      avisarEstado();
      if (releAoInscrever) { releAoInscrever = false; agendarDisparo(); }
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      caiu();
    }
  }
  function conectar() {
    querAberto = true;
    if (!ativo || canal || conectando) return;
    conectando = true;
    limpar('reconexao');
    // espera o canal anterior sair (o cliente devolve o mesmo canal enquanto o tópico existe)
    remocao
      .then(() => criarCliente())
      .then((c) => {
        conectando = false;
        if (!ativo || !querAberto || canal) return;
        cliente = c;
        const ch = c.channel(TOPICO, { config: { broadcast: { self: false, ack: false }, private: false } });
        canal = ch;
        ch.on('broadcast', { event: EVENTO }, (msg) => { if (canal === ch) recebeu(msg?.payload?.t); });
        ch.subscribe((status) => mudouStatus(ch, status));
      })
      .catch(() => {
        conectando = false;
        caiu();
      });
  }
  function fechar() {
    querAberto = false;
    limpar('reconexao');
    if (canal) releAoInscrever = true;
    soltar();
    avisarEstado();
  }

  function aoMudarVisibilidade() {
    if (!ativo) return;
    if (visivel()) {
      limpar('folga');
      if (!canal && !conectando) { tentativa = 0; conectar(); }
      return;
    }
    // escondida: nada de releitura pendente; o canal fecha depois da folga
    limpar('espera'); limpar('balde');
    inicioRajada = 0;
    pendente = false;
    if (tm.folga == null) tm.folga = relogio.setTimeout(() => { tm.folga = null; if (!visivel()) fechar(); }, folga);
  }

  return {
    iniciar() {
      if (ativo) return;
      ativo = true;
      doc?.addEventListener?.('visibilitychange', aoMudarVisibilidade);
      if (visivel()) conectar();
    },
    parar() {
      if (!ativo) return;
      ativo = false;
      doc?.removeEventListener?.('visibilitychange', aoMudarVisibilidade);
      for (const k of Object.keys(tm)) limpar(k);
      pendente = false;
      fechar();
    },
    aoVivo,
    conectado: () => inscrito,
  };
}
